import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Edit, Clock, Save, History } from "lucide-react";
import { format } from "date-fns";
import { EndpointKeys } from "@/config/api";
import ApiService from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import { 
  EmployeeSchedule, 
  ScheduleHistoryItem, 
  ScheduleDays, 
  UpdateScheduleRequest 
} from "@/types/workingHours";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import { useParams, useSearchParams } from "react-router-dom";

const DEFAULT_SCHEDULE: ScheduleDays = {
  Monday: 8,
  Tuesday: 8,
  Wednesday: 8,
  Thursday: 8,
  Friday: 8,
  Saturday: 0,
  Sunday: 0,
};

interface WorkingHoursScheduleProps {
  employeeId?: string;
}

const WorkingHoursSchedule: React.FC<WorkingHoursScheduleProps> = ({ employeeId }) => {
  const [currentSchedule, setCurrentSchedule] = useState<EmployeeSchedule | null>(null);
  const [scheduleHistory, setScheduleHistory] = useState<ScheduleHistoryItem[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [editedHours, setEditedHours] = useState<ScheduleDays>({
    Monday: 0,
    Tuesday: 0,
    Wednesday: 0,
    Thursday: 0,
    Friday: 0,
    Saturday: 0,
    Sunday: 0,
  });
  const [changeReason, setChangeReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperUserAccount = isSuperUser(user);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const { employeeId: employeeIdFromParams } = useParams<{ employeeId: string }>();
  const effectiveEmployeeId = employeeId || employeeIdFromParams;
  
  const [searchParams] = useSearchParams();
  
  // Check for userId param in URL which would be set when a superuser is accessing the page
  useEffect(() => {
    const userIdParam = searchParams.get('userId');
    if (isSuperUserAccount && userIdParam) {
      setSelectedUserId(userIdParam);
    }
  }, [searchParams, isSuperUserAccount]);

  useEffect(() => {
    if (effectiveEmployeeId) {
      fetchCurrentSchedule();
    }
  }, [effectiveEmployeeId, selectedUserId]);

  const fetchCurrentSchedule = async () => {
    if (!effectiveEmployeeId) return;
    
    setIsLoading(true);
    try {
      const response = await ApiService.request<EmployeeSchedule>(
        EndpointKeys.GET_EMPLOYEE_SCHEDULE,
        {
          urlParams: {
            employee_id: effectiveEmployeeId,
          },
          queryParams: {
            include_weekly_total: "true",
            ...(isSuperUserAccount && selectedUserId !== "current" && {
              userId: selectedUserId
            })
          }
        }
      );
      
      if (response) {
        setCurrentSchedule(response);
        setEditedHours(response.days);
      }
    } catch (error) {
      console.error("Error fetching current schedule:", error);
      // Don't show error toast since employee might not have a schedule yet
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScheduleHistory = async () => {
    if (!effectiveEmployeeId) return;
    
    setIsLoading(true);
    try {
      const response = await ApiService.request<ScheduleHistoryItem[]>(
        EndpointKeys.GET_EMPLOYEE_SCHEDULE_HISTORY,
        {
          urlParams: {
            employee_id: effectiveEmployeeId,
          },
          ...(isSuperUserAccount && selectedUserId !== "current" && {
            queryParams: { userId: selectedUserId }
          })
        }
      );
      
      if (response) {
        setScheduleHistory(response);
        setIsHistoryModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching schedule history:", error);
      toast({
        title: "Error",
        description: "Failed to fetch schedule history",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSchedule = async () => {
    if (!effectiveEmployeeId || !changeReason.trim()) {
      toast({
        title: "Validation Error",
        description: "Please provide a reason for the schedule change",
        variant: "destructive",
      });
      return;
    }
    
    // Validate hours are non-negative
    const hasNegativeHours = Object.values(editedHours).some(hours => hours < 0);
    if (hasNegativeHours) {
      toast({
        title: "Validation Error",
        description: "Hours cannot be negative",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    try {
      const request: UpdateScheduleRequest = {
        days: editedHours,
        change_reason: changeReason,
      };
      
      const response = await ApiService.request<EmployeeSchedule>(
        EndpointKeys.UPDATE_EMPLOYEE_SCHEDULE,
        {
          method: "POST",
          body: request,
          urlParams: {
            employee_id: effectiveEmployeeId,
          },
          ...(isSuperUserAccount && selectedUserId !== "current" && {
            queryParams: { userId: selectedUserId }
          })
        }
      );
      
      if (response) {
        setCurrentSchedule({
          ...response,
          change_info: currentSchedule?.change_info || {
            reason: changeReason,
            reason_type: "USER_INPUT",
            changed_at: new Date().toISOString(),
            changed_by_id: 0,
            changed_by_email: "",
          }
        });
        
        setIsEditModalOpen(false);
        setChangeReason("");
        
        toast({
          title: "Success",
          description: "Working hours schedule updated successfully",
        });
      }
    } catch (error) {
      console.error("Error updating schedule:", error);
      toast({
        title: "Error",
        description: "Failed to update working hours schedule",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (day: keyof ScheduleDays, value: string) => {
    const numValue = parseFloat(value);
    setEditedHours({
      ...editedHours,
      [day]: isNaN(numValue) ? 0 : numValue,
    });
  };

  const openEditModal = () => {
    if (currentSchedule) {
      setEditedHours(currentSchedule.days);
    }
    setIsEditModalOpen(true);
  };

  const calculateWeeklyTotal = (days: ScheduleDays): number => {
    return Object.values(days).reduce((total, hours) => total + hours, 0);
  };

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    } catch (error) {
      return "Unknown date";
    }
  };

  const noScheduleMessage = (
    <div className="p-4 bg-gray-50 rounded-md">
      <div className="text-center mb-4">
        <p className="text-gray-500 mb-2">No working hours schedule has been set for this employee.</p>
        <p className="text-gray-600 text-sm">
          The default schedule of Monday-Friday, 8 hours per day (40 hours per week) will be used for earnings calculations.
        </p>
      </div>
      <div className="mb-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Day</TableHead>
              <TableHead className="text-right">Default Hours</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(DEFAULT_SCHEDULE).map(([day, hours]) => (
              <TableRow key={day}>
                <TableCell>{day}</TableCell>
                <TableCell className="text-right">{hours}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="text-center">
        <Button onClick={openEditModal}>
          <Clock className="mr-2 h-4 w-4" />
          Set Custom Working Hours
        </Button>
      </div>
    </div>
  );

  const scheduleContent = currentSchedule ? (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Typical Working Hours</h3>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={fetchScheduleHistory}>
            <History className="mr-2 h-4 w-4" />
            View History
          </Button>
          <Button onClick={openEditModal}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Hours
          </Button>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-md">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-500">Weekly Total</p>
            <p className="font-medium">{currentSchedule.weekly_total || calculateWeeklyTotal(currentSchedule.days)} hours</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Last Updated</p>
            <p className="font-medium">{formatDate(currentSchedule.change_info.changed_at)}</p>
          </div>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Day</TableHead>
            <TableHead className="text-right">Hours</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(currentSchedule.days).map(([day, hours]) => (
            <TableRow key={day}>
              <TableCell>{day}</TableCell>
              <TableCell className="text-right">{hours}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  ) : noScheduleMessage;

  return (
    <div className="mt-8 border-t pt-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2">Working Hours Schedule</h2>
        <p className="text-gray-600 text-sm">
          The default schedule is Monday-Friday, 8 hours per day (40 hours per week). This will be used for earnings calculations unless a custom schedule is set.
        </p>
      </div>
      
      {isLoading ? (
        <div className="p-4 text-center">
          <p>Loading schedule information...</p>
        </div>
      ) : (
        scheduleContent
      )}

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Working Hours</DialogTitle>
            <DialogDescription>
              Set the typical working hours for each day of the week. If not set, the default schedule of Monday-Friday, 8 hours per day will be used.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex justify-between items-center mb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditedHours(DEFAULT_SCHEDULE)}
              >
                Use Default Schedule
              </Button>
              <span className="text-sm text-gray-500">
                Default: 40 hours/week (M-F)
              </span>
            </div>
            
            {Object.keys(editedHours).map((day) => (
              <div key={day} className="grid grid-cols-2 items-center gap-4">
                <Label htmlFor={`hours-${day}`}>{day}</Label>
                <Input
                  id={`hours-${day}`}
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={editedHours[day as keyof ScheduleDays]}
                  onChange={(e) => handleInputChange(day as keyof ScheduleDays, e.target.value)}
                />
              </div>
            ))}
            
            <div className="mt-6">
              <Label htmlFor="change-reason">Reason for Change</Label>
              <Input
                id="change-reason"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="E.g., Contract update, Schedule adjustment"
                className="mt-1"
              />
            </div>
            
            <div className="mt-4 text-sm text-gray-500">
              <p>Weekly Total: {calculateWeeklyTotal(editedHours)} hours</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpdateSchedule}
              disabled={isLoading || !changeReason.trim()}
            >
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={isHistoryModalOpen} onOpenChange={setIsHistoryModalOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Working Hours History</DialogTitle>
            <DialogDescription>
              View previous versions of the working hours schedule.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {scheduleHistory.length === 0 ? (
              <p className="text-center text-gray-500">No history available.</p>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {scheduleHistory.map((item) => (
                  <AccordionItem key={item.id} value={`version-${item.id}`}>
                    <AccordionTrigger>
                      <div className="flex justify-between items-center w-full pr-4">
                        <span>Version {item.version}</span>
                        <span className="text-sm text-gray-500">
                          {formatDate(item.change_info.changed_at)}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-md">
                          <p><strong>Reason:</strong> {item.change_info.reason}</p>
                          <p><strong>Changed By:</strong> {item.change_info.changed_by_email}</p>
                          <p><strong>Weekly Total:</strong> {calculateWeeklyTotal(item.days)} hours</p>
                        </div>
                        
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Day</TableHead>
                              <TableHead className="text-right">Hours</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Object.entries(item.days).map(([day, hours]) => (
                              <TableRow key={`${item.id}-${day}`}>
                                <TableCell>{day}</TableCell>
                                <TableCell className="text-right">{hours}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkingHoursSchedule; 